/**
 * API interceptor — wraps window.fetch (and optionally axios) to record
 * request duration, status, and errors via frontendAnalytics.
 *
 * Auto-attaches on import. Pass an axios instance to `attachAxios(instance)`
 * to also instrument that axios client.
 */

import { trackEvent } from './frontendAnalytics';

const SKIP_URL_PATTERNS = [
    /\/api\/frontend\/events/,
];

function shouldSkip(url) {
    if (!url) return true;
    const s = String(url);
    return SKIP_URL_PATTERNS.some(rx => rx.test(s));
}

function record(method, url, status, duration, errored) {
    if (shouldSkip(url)) return;
    trackEvent('apiCall', {
        method: String(method || 'GET').toUpperCase(),
        url:    String(url).slice(0, 500),
        status: status || 0,
        duration: Math.max(0, Math.round(duration)),
        error:  !!errored,
    });
}

function wrapFetch() {
    if (typeof window === 'undefined' || !window.fetch || window.fetch.__wf_wrapped) return;
    const orig = window.fetch.bind(window);
    const wrapped = async function (input, init) {
        const start = performance.now();
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = (init && init.method) || (input && input.method) || 'GET';
        try {
            const res = await orig(input, init);
            record(method, url, res.status, performance.now() - start, !res.ok);
            return res;
        } catch (err) {
            record(method, url, 0, performance.now() - start, true);
            throw err;
        }
    };
    wrapped.__wf_wrapped = true;
    window.fetch = wrapped;
}

function attachAxios(axiosInstance) {
    if (!axiosInstance || !axiosInstance.interceptors) return;
    axiosInstance.interceptors.request.use((cfg) => {
        cfg.__wfStart = performance.now();
        return cfg;
    });
    axiosInstance.interceptors.response.use(
        (res) => {
            const start = res.config && res.config.__wfStart;
            const dur = start ? performance.now() - start : 0;
            record(res.config?.method, res.config?.url, res.status, dur, false);
            return res;
        },
        (err) => {
            const cfg = err.config || {};
            const start = cfg.__wfStart;
            const dur = start ? performance.now() - start : 0;
            const status = err.response?.status || 0;
            record(cfg.method, cfg.url, status, dur, true);
            return Promise.reject(err);
        },
    );
}

wrapFetch();

export { attachAxios };
export default { attachAxios };
